"use client"

import React, {useEffect, useLayoutEffect, useRef} from "react";
import {motion, useAnimationControls, useReducedMotion} from "framer-motion";
import {cn} from "@/lib/utils";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

type State = "collapsed" | "expanded";

export const AnimatedAsset7 = ({
  className = "",
  language,
}: {
  className?: string;
  language?: "en" | "de";
}) => {
  const controls = useAnimationControls();
  const prefersReduced = useReducedMotion();
  const stateRef = useRef<State>("collapsed");
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setState = (next: State) => {
    if (stateRef.current === next) return Promise.resolve();
    stateRef.current = next;
    return controls.start(next);
  };

  useEffect(() => {
    controls.set("collapsed");
    stateRef.current = "collapsed";
  }, [controls]);

  useLayoutEffect(() => {
    if (prefersReduced) return;
    const el = rootRef.current;
    if (!el) return;

    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

    if (isTouchDevice) {
      const st = ScrollTrigger.create({
        trigger: el,
        start: "top 50%",
        end: "bottom 50%",
        onEnter: () => setState("expanded"),
        onLeave: () => {},
        onEnterBack: () => setState("expanded"),
        onLeaveBack: () => setState("collapsed"),
        invalidateOnRefresh: true,
      });

      return () => st.kill();
    }

    return;
  }, [prefersReduced, controls]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseEnter = async () => {
    if (prefersReduced) return;
    await setState("expanded");
  };

  const handleMouseLeave = () => {
    if (prefersReduced) return;
    setState("collapsed");
  };

  const ease = {ease: "circOut", duration: .75};

  const gridSides = {
    collapsed: {y: 0, transition: ease},
    expanded: {y: "-20rem", transition: ease},
  }

  const gridCenter = {
    collapsed: {y: 0, transition: ease},
    expanded: {y: "60rem", transition: ease},
  }

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full h-[50rem] bg-[#0A1A1D] flex flex-col items-center justify-center px-[2rem] overflow-clip",
        "relative after:absolute after:h-[2px] after:w-full after:left-0 after:bottom-0 after:bg-[#D9F9B9]",
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 flex items-center gap-[1rem]">
        <motion.div
          className="w-[19rem] flex flex-col gap-[1rem]"
          variants={gridSides}
          animate={controls}
          initial="collapsed"
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="w-full h-[19rem] border border-[#B9C0F9]/20 rounded-[1rem]"
            />
          ))}
        </motion.div>

        <motion.div
          className="w-[19rem] flex flex-col gap-[1rem]"
          variants={gridCenter}
          animate={controls}
          initial="collapsed"
        >
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="w-full h-[19rem] border border-[#B9C0F9]/20 rounded-[1rem]"
            />
          ))}

          <div className="w-full h-[19rem] bg-[#D9F9B9] rounded-[1rem] flex flex-col items-start justify-between p-[2rem]">
            <p className="mb-0 leading-none font-funnel text-black/20 font-medium text-[4.75rem]">148</p>
            <p className="mb-0 leading-[1.1] text-black/70 font-medium text-[2.25rem]">Trades<br/>{language === "en" ? "this month" : "im Monat"}</p>
          </div>

          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="w-full h-[19rem] border border-[#B9C0F9]/20 rounded-[1rem]"
            />
          ))}

          <div className="w-full h-[19rem] bg-[#D9F9B9] rounded-[1rem] flex flex-col items-start justify-between p-[2rem]">
            <p className="mb-0 leading-none font-funnel text-black/20 font-medium text-[4.75rem]">6</p>
            <p className="mb-0 leading-[1.1] text-black/70 font-medium text-[2.25rem]">Trades<br/>{language === "en" ? "today" : "heute"}</p>
          </div>

          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="w-full h-[19rem] border border-[#B9C0F9]/20 rounded-[1rem]"
            />
          ))}
        </motion.div>

        <motion.div
          className="w-[19rem] flex flex-col gap-[1rem]"
          variants={gridSides}
          animate={controls}
          initial="collapsed"
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="w-full h-[19rem] border border-[#B9C0F9]/20 rounded-[1rem]"
            />
          ))}
        </motion.div>
      </div>

    </div>
  );
};
